import { useMemo, useState } from "react";
import type { AppEvent } from "../features/calendar/types";
import { useT } from "../features/i18n/useT";
import { usePageTag } from "../app/usePageTag";
import { useEvents } from "../features/calendar/useEvents";
import { isLesson, isDeadlineLike, sameDay } from "../features/calendar/selectors";
import { formatTime, formatDate, addDays, addMonths, startOfWeekMonday, getWeekDays } from "../shared/utils/date";
import { openMapWithRoom } from "../features/map/roomLink";
import { HiddenSubjectsSheet } from "../features/settings/HiddenSubjectsSheet";

type Mode = "day" | "week" | "month";
type DaySummary = { lessons: number; deadlines: number; lessonTitle?: string; deadlineTitle?: string };

function formatLessonType(type: string | undefined, t: (k: string) => string) {
  const raw = (type || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw.includes("lec") || raw.includes("лекц")) return t("schedule.lessonType.lecture");
  if (raw.includes("prac") || raw.includes("практ")) return t("schedule.lessonType.practice");
  return type || "";
}

function dayKey(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isHiddenWeekDay(d: Date) {
  return d.getFullYear() === 2026 && d.getMonth() === 1 && d.getDate() === 1;
}

function formatTermDate(d: Date) {
  return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

function currentDuTermRange(now = new Date()): { start: Date; end: Date } | null {
  const y = now.getFullYear();
  const m = now.getMonth() + 1;

  if (m === 9 || m === 10 || m === 11) {
    return { start: new Date(y, 8, 1), end: new Date(y, 10, 30) };
  }

  if (m === 12) {
    return { start: new Date(y, 11, 1), end: new Date(y + 1, 1, 27) };
  }
  if (m === 1 || m === 2) {
    return { start: new Date(y - 1, 11, 1), end: new Date(y, 1, 27) };
  }

  if (m === 3 || m === 4 || m === 5) {
    return { start: new Date(y, 2, 1), end: new Date(y, 4, 30) };
  }

  return null;
}

export default function SchedulePage() {
  usePageTag("schedule");
  const t = useT();
  const events = useEvents();
  const eventsMonth = useEvents({ includeOnline: true });

  const [mode, setMode] = useState<Mode>("day");
  const [cursor, setCursor] = useState(() => new Date());
  const [hiddenOpen, setHiddenOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  const weekStart = useMemo(() => startOfWeekMonday(cursor), [cursor]);
  const monthStart = useMemo(() => new Date(cursor.getFullYear(), cursor.getMonth(), 1), [cursor]);
  const calendarStart = useMemo(() => startOfWeekMonday(monthStart), [monthStart]);
  const calendarDays = useMemo(() => Array.from({ length: 42 }, (_, i) => addDays(calendarStart, i)), [calendarStart]);

  const daySummary = useMemo(() => {
    const map = new Map<string, DaySummary>();
    const bump = (d: Date, key: "lessons" | "deadlines", title: string) => {
      const k = dayKey(d);
      const cur = map.get(k) || { lessons: 0, deadlines: 0 };
      cur[key] += 1;
      if (key === "lessons" && !cur.lessonTitle) cur.lessonTitle = title;
      if (key === "deadlines" && !cur.deadlineTitle) cur.deadlineTitle = title;
      map.set(k, cur);
    };

    for (const e of eventsMonth) {
      if (isLesson(e)) bump(new Date(e.start), "lessons", e.title);
      if (isDeadlineLike(e)) bump(new Date(e.due || e.start), "deadlines", e.title);
    }
    return map;
  }, [eventsMonth]);

  const lessons = useMemo(() => {
    return events.filter(isLesson).slice().sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  }, [events]);
  const hiddenEvents = useMemo(() => {
    return events.filter((e) => e.kind === "lesson" && e.source?.provider === "group");
  }, [events]);

  const dayLessons = useMemo(() => {
    return lessons.filter((e) => sameDay(new Date(e.start), cursor));
  }, [lessons, cursor]);

  const weekDays = useMemo(() => getWeekDays(weekStart).filter((d) => !isHiddenWeekDay(d)), [weekStart]);

  const weekLessons = useMemo(() => {
    const map = new Map<string, AppEvent[]>();
    for (const d of weekDays) map.set(d.toDateString(), []);
    for (const e of lessons) {
      const dt = new Date(e.start);
      for (const d of weekDays) {
        if (sameDay(dt, d)) {
          map.get(d.toDateString())!.push(e);
          break;
        }
      }
    }
    for (const arr of map.values()) arr.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    return map;
  }, [lessons, weekDays]);
  const weekDayNames = useMemo(() => {
    const base = startOfWeekMonday(new Date());
    return Array.from({ length: 7 }, (_, i) => addDays(base, i).toLocaleDateString([], { weekday: "short" }));
  }, []);

  const dateLabel = useMemo(() => {
    if (mode === "day") return formatDate(cursor);
    if (mode === "week") return `${formatDate(weekStart)} - ${formatDate(addDays(weekStart, 6))}`;
    const term = currentDuTermRange(new Date());
    if (term) return `${formatTermDate(term.start)} - ${formatTermDate(term.end)}`;
    return monthStart.toLocaleDateString([], { month: "long", year: "numeric" });
  }, [mode, cursor, weekStart, monthStart]);

  const monthDayItems = useMemo(() => {
    if (!selectedDay) return [];
    const list = eventsMonth.filter((e) => {
      if (isLesson(e)) return sameDay(new Date(e.start), selectedDay);
      if (isDeadlineLike(e)) return sameDay(new Date(e.due || e.start), selectedDay);
      return false;
    });
    return list;
  }, [eventsMonth, selectedDay]);

  return (
    <div className="page schedule-page">
      <div className="schedule-header">
        <h2>{t("schedule.title")}</h2>

        <div className="segmented schedule-segmented">
          <button className={`seg ${mode === "day" ? "is-active" : ""}`} onClick={() => setMode("day")}>
            {t("common.day")}
          </button>
          <button className={`seg ${mode === "week" ? "is-active" : ""}`} onClick={() => setMode("week")}>
            {t("common.week")}
          </button>
          <button className={`seg ${mode === "month" ? "is-active" : ""}`} onClick={() => setMode("month")}>
            {t("common.month")}
          </button>
        </div>
      </div>

      <div className="card schedule-toolbar">
        <div className="schedule-toolbar-inner">
          <div className="schedule-date muted small">
            {dateLabel}
          </div>

          <div className="schedule-controls">
            <button
              className="btn ghost schedule-icon-btn"
              onClick={() =>
                setCursor((d) => (mode === "day" ? addDays(d, -1) : mode === "week" ? addDays(d, -7) : addMonths(d, -1)))
              }
              aria-label={t("common.prev")}
            >
              &larr;
            </button>
            <button className="btn ghost" onClick={() => setCursor(new Date())}>
              {t("common.today")}
            </button>
            <button
              className="btn ghost schedule-icon-btn"
              onClick={() =>
                setCursor((d) => (mode === "day" ? addDays(d, 1) : mode === "week" ? addDays(d, 7) : addMonths(d, 1)))
              }
              aria-label={t("common.next")}
            >
              &rarr;
            </button>
            <button
              className="btn ghost schedule-icon-btn schedule-hidden-btn"
              onClick={() => setHiddenOpen((v) => !v)}
              aria-label={t("settings.manageHidden")}
              title={t("settings.manageHidden")}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z"
                  fill="currentColor"
                  opacity="0.7"
                />
                <path
                  d="M12 15.5A3.5 3.5 0 1 1 12 8a3.5 3.5 0 0 1 0 7.5Z"
                  fill="currentColor"
                />
                <path
                  d="M4 4 20 20"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <HiddenSubjectsSheet open={hiddenOpen} onClose={() => setHiddenOpen(false)} events={hiddenEvents} />

      {mode === "day" ? (
        dayLessons.length === 0 ? (
          <div className="empty schedule-empty">
            <div className="empty-title">{t("schedule.noLessonsDay.title")}</div>
            <div className="muted small">{t("schedule.noLessonsDay.sub")}</div>
          </div>
        ) : (
          <div className="schedule-lesson-list">
            {dayLessons.map((e) => (
              <div key={e.uid} className="schedule-lesson-card">
                <div className="lesson-time">
                  <div className="time-range">
                    {formatTime(e.start)} - {e.end ? formatTime(e.end) : "--:--"}
                  </div>
                </div>

                <div className="content">
                  <div className="title-row">
                    <div className="title">{e.title}</div>
                  </div>
                  
                  <div className="meta">
                    {e.lessonType ? <span className="pill pill-off">{formatLessonType(e.lessonType, t)}</span> : null}
                    {e.teacher ? <span className="pill pill-on">{e.teacher}</span> : null}
                    {e.room ? (
                      <button
                        className="pill pill-off"
                        onClick={() => {
                          if (e.room) openMapWithRoom(e.room);
                        }}
                        style={{ border: "none", background: "inherit", cursor: "pointer" }}
                      >
                        {e.room}
                      </button>
                    ) : (
                      <span className="pill pill-muted">{t("common.noRoom")}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : mode === "week" ? (
        <div className="week-grid">
          {weekDays.map((d) => {
            const arr = weekLessons.get(d.toDateString()) || [];
            const isToday = sameDay(d, new Date());
            return (
              <div key={d.toDateString()} className={`week-col ${isToday ? "is-today" : ""}`}>
                <button
                  className="week-head"
                  onClick={() => {
                    setCursor(d);
                    setMode("day");
                  }}
                  title={t("schedule.openDay")}
                >
                  {formatDate(d)}
                </button>

                {arr.length === 0 ? (
                  <div className="week-empty">{t("schedule.noLessons")}</div>
                ) : (
                  <div className="week-items">
                    {arr.map((e) => (
                      <div key={e.uid} className="week-item">
                        <div className="wk-time">
                          {formatTime(e.start)} - {e.end ? formatTime(e.end) : "--:--"}
                        </div>
                        <div className="wk-title">{e.title}</div>
                        {e.lessonType ? <div className="muted small">{formatLessonType(e.lessonType, t)}</div> : null}
                        <div className="wk-meta">
                          <button className="link" onClick={() => e.room && openMapWithRoom(e.room)}>
                            {e.room || t("common.noRoom")}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="month-panel">
          <div className="month-weekdays">
            {weekDayNames.map((name) => (
              <div key={name} className="month-weekday">
                {name}
              </div>
            ))}
          </div>

          <div className="month-grid">
            {calendarDays.map((d) => {
              const info = daySummary.get(dayKey(d));
              const hasLessons = Boolean(info?.lessons);
              const hasDeadlines = Boolean(info?.deadlines);
              const isToday = sameDay(d, new Date());
              const isOutside = d.getMonth() !== monthStart.getMonth();
              const statusClass = hasLessons && hasDeadlines ? "has-both" : hasLessons ? "has-lesson" : hasDeadlines ? "has-deadline" : "";

              return (
                <button
                  key={d.toDateString()}
                  className={`month-day ${isOutside ? "is-outside" : ""} ${isToday ? "is-today" : ""} ${statusClass}`}
                  onClick={() => {
                    setCursor(d);
                    setSelectedDay(d);
                  }}
                  title={formatDate(d)}
                >
                  <span className="day-num">{d.getDate()}</span>
                  {hasLessons || hasDeadlines ? (
                    <>
                      <div className="month-badges">
                        {hasLessons ? <span className="month-badge is-lesson">{info?.lessons} L</span> : null}
                        {hasDeadlines ? <span className="month-badge is-deadline">{info?.deadlines} D</span> : null}
                      </div>
                      <div className="month-details">
                        {info?.lessonTitle ? <span className="month-detail is-lesson">{info.lessonTitle}</span> : null}
                        {info?.deadlineTitle ? <span className="month-detail is-deadline">{info.deadlineTitle}</span> : null}
                      </div>
                    </>
                  ) : null}
                </button>
              );
            })}
          </div>

          {selectedDay ? (
            <div className="month-popover">
              <div className="month-popover-head">
                <div className="title">{formatDate(selectedDay)}</div>
                <button className="btn ghost" onClick={() => setSelectedDay(null)} aria-label={t("common.close")}>
                  x
                </button>
              </div>
              {monthDayItems.length === 0 ? (
                <div className="muted small">{t("schedule.dayEmpty")}</div>
              ) : (
                <div className="month-popover-list">
                  {monthDayItems.map((e) => (
                    <div key={e.uid} className={`month-popover-item ${e.kind}`}>
                      <div className="mp-time">
                        {isLesson(e) ? formatTime(e.start) : formatTime(e.due || e.start)}
                      </div>
                      <div className="mp-body">
                        <div className="mp-title">{e.title}</div>
                        <div className="mp-meta">
                          {isLesson(e) && e.lessonType ? (
                            <span className="pill pill-off">{formatLessonType(e.lessonType, t)}</span>
                          ) : null}
                          {isDeadlineLike(e) ? (
                            <span className="pill pill-on">{t("deadlines.kind.deadline")}</span>
                          ) : null}
                          {e.room ? (
                            <button className="pill pill-off" onClick={() => openMapWithRoom(e.room!)}>
                              {e.room}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
